import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { OrganisationTypeConstants, ProjectGroupConstants } from "../../../../../../../../constants";
import { SelectData } from "../../../../../../../../models";
import { deleteGroup, updateProjectGroup } from "../../../../../../../../service/project";
import { lookUp } from "../../../../../../../../service/query";
import { ServiceError, Status } from "../../../../../../../../service/Shared";
import { useAuth } from "../../../../../../../../useAuth";
import { formatCoordinates, splitCoordinates, useIsLoadingWrapper } from "../../../../../../../../utils";
import { getDashboardRoute } from "../../../../../../../../utils/routes";
import { Toast } from "../../../../../../../../widget";
import { GroupContext } from "../../GroupContext";

export type ProjectGroupData = {
  groupId: string;
  groupName: string;
  rowVersion: number;
};

export type OverviewFormData = {
  projectGroupDescription: string | null;
  groupName: string | null;
  areaGrossHa: string | null;
  region: string | null;
  latitude: string | null;
  longitude: string | null;
  country: string | null;
  externalReference: string | null;
  externalParty: string | null;
};

const initialFormData: OverviewFormData = {
  projectGroupDescription: null,
  groupName: null,
  areaGrossHa: null,
  region: null,
  latitude: null,
  longitude: null,
  country: null,
  externalReference: null,
  externalParty: null,
};

interface UseOverviewSectionReturnData {
  projectGroupData?: ProjectGroupData;
  formData: OverviewFormData;
  countryOptions: SelectData;
  errors: ServiceError[];
  setField: <K extends keyof OverviewFormData>(key: K) => (value: OverviewFormData[K]) => void;
  onSubmit: () => void;
  isOnSubmitLoading: boolean;
  isLandholdingManagement: boolean;
  showDeleteModal: boolean;
  setShowDeleteModal: Dispatch<SetStateAction<boolean>>;
  onDeleteConfirm: () => void;
  isDeleteLoading: boolean;
  deleteErrors: ServiceError[];
}

export const useOverviewSection = (): UseOverviewSectionReturnData => {
  const navigate = useNavigate();
  const { currentUserType } = useAuth();
  const { groupDetails, groupType } = useContext(GroupContext);
  const isLandholdingManagement = groupType === ProjectGroupConstants.GROUP_TYPE_LANDHOLDING;

  const [projectGroupData, setProjectGroupData] = useState<ProjectGroupData | undefined>();
  const [formData, setFormData] = useState<OverviewFormData>(initialFormData);
  const [countryOptions, setCountryOptions] = useState<SelectData>([]);
  const [isOnSubmitLoading, setIsOnSubmitLoading] = useState(false);
  const [errors, setErrors] = useState<ServiceError[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [deleteErrors, setDeleteErrors] = useState<ServiceError[]>([]);

  const setField =
    <K extends keyof OverviewFormData>(key: K) =>
    (value: OverviewFormData[K]) => {
      setFormData((prev) => ({ ...prev, [key]: value }));
    };

  const onSubmit = useIsLoadingWrapper(async (): Promise<void> => {
    if (groupDetails?.groupGuid && projectGroupData?.rowVersion !== undefined) {
      const locationCoordinates = formatCoordinates(formData.latitude, formData.longitude);

      const response = await updateProjectGroup({
        addressCountryCode: formData.country,
        addressRegion: formData.region,
        areaNetHa: groupDetails.areaNetHa,
        areaGrossHa: formData.areaGrossHa !== null ? parseFloat(formData.areaGrossHa) : null,
        locationCoordinates,
        locationGridReference: null,
        externalReference: formData.externalReference,
        externalParty: formData.externalParty,
        name: formData.groupName,
        description: formData.projectGroupDescription,
        groupUuid: groupDetails.groupGuid,
        rowVersion: projectGroupData.rowVersion,
      });

      setErrors([]);

      if (response.status === Status.Success && response.data) {
        Toast.success({ message: "Landholding updated successfully" });
        setProjectGroupData({
          ...projectGroupData,
          rowVersion: response.data.rowVersion,
        });
      }
      if (response.status === Status.Error && response.errors) {
        setErrors(response.errors);
      }
    }
  }, setIsOnSubmitLoading);

  const onDeleteConfirm = useIsLoadingWrapper(async (): Promise<void> => {
    if (groupDetails?.groupGuid && projectGroupData?.rowVersion !== undefined) {
      const response = await deleteGroup({
        groupUuid: groupDetails.groupGuid,
        rowVersion: projectGroupData.rowVersion,
      });

      setDeleteErrors([]);

      if (response.status === Status.Success) {
        Toast.success({ message: "Landholding deleted successfully" });
        navigate(getDashboardRoute(currentUserType as OrganisationTypeConstants));
      }
      if (response.status === Status.Error && response.errors) {
        setDeleteErrors(response.errors);
      }
    }
  }, setIsDeleteLoading);

  const fetchCountryOptions = useCallback(async (): Promise<void> => {
    const res = await lookUp({ type: "Country", sort: [{ key: "valueString", direction: "asc" }] });
    if (res.status === Status.Success && res.data) {
      setCountryOptions(
        res.data.filter((c) => c.key.includes("GB-")).map((c) => ({ key: c.key, value: c.valueString ?? c.key }))
      );
    }
  }, []);

  useEffect(() => {
    fetchCountryOptions();
  }, [fetchCountryOptions]);

  useEffect(() => {
    if (groupDetails) {
      setProjectGroupData({
        groupId: groupDetails.externalReference,
        groupName: groupDetails.groupName,
        rowVersion: groupDetails.rowVersion,
      });
      setFormData({
        projectGroupDescription: groupDetails.groupDescription,
        groupName: groupDetails.groupName,
        areaGrossHa: groupDetails.areaGrossHa !== null ? String(groupDetails.areaGrossHa) : null,
        region: groupDetails.addressRegion,
        ...splitCoordinates(groupDetails.locationCoordinates),
        country: groupDetails.addressCountryCode,
        externalReference: groupDetails.externalReference,
        externalParty: groupDetails.externalParty,
      });
    }
  }, [groupDetails]);

  return {
    projectGroupData,
    formData,
    countryOptions,
    errors,
    setField,
    onSubmit,
    isOnSubmitLoading,
    isLandholdingManagement,
    showDeleteModal,
    setShowDeleteModal,
    onDeleteConfirm,
    isDeleteLoading,
    deleteErrors,
  };
};
